import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { VendorsListingService } from '../../services/vendors.services';

interface DefaultFeeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DefaultFeeModal: React.FC<DefaultFeeModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [currentFee, setCurrentFee] = useState<number | null>(null);
  const [newFee, setNewFee] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Fetch current default fee when modal opens
  useEffect(() => {
    if (open) {
      fetchDefaultFee();
    }
  }, [open]);

  const fetchDefaultFee = async () => {
    setFetching(true);
    setError('');
    try {
      const data = await VendorsListingService.getDefaultFee();
      console.log('Default fee response:', data);
      
      // Service now returns { defaultFeePercentage: number }
      const feeValue = data.defaultFeePercentage;
      
      setCurrentFee(feeValue);
      setNewFee(feeValue.toString());
    } catch (err: any) {
      console.error('Error fetching default fee:', err);
      setError('Failed to load current default fee');
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async () => {
    setError('');

    // Validate input
    const feeValue = parseFloat(newFee);
    if (isNaN(feeValue)) {
      setError('Please enter a valid number');
      return;
    }

    if (feeValue < 0 || feeValue > 100) {
      setError('Fee percentage must be between 0 and 100');
      return;
    }

    setLoading(true);
    try {
      await VendorsListingService.updateDefaultFee(feeValue);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error('Error updating default fee:', err);
      setError(err.response?.data?.message || 'Failed to update default fee');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setNewFee('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Default Platform Fee</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {fetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                This fee applies to all vendors who don't have a custom fee set.
                Vendors with custom fees will not be affected.
              </Typography>

              {currentFee !== null && (
                <Alert severity="info">
                  Current default fee: <strong>{currentFee}%</strong>
                </Alert>
              )}

              <TextField
                label="New Default Fee (%)"
                type="number"
                value={newFee}
                onChange={(e) => setNewFee(e.target.value)}
                fullWidth
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 0.1,
                }}
                helperText="Enter a percentage between 0 and 100"
                disabled={loading}
              />

              {error && (
                <Alert severity="error" onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Example:</strong> With a 5% fee on an order of 890 QAR:
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  • Platform fee: 44.50 QAR
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  • Vendor earnings: 845.50 QAR
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || fetching || !newFee}
        >
          {loading ? <CircularProgress size={24} /> : 'Update Fee'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
